import * as React from "react";
import {Redirect} from "react-router-dom";
import {cx} from "emotion";

import "./my-listings-route.scss";

import {XdSentry}           from "@Common/libs/sentry/xd-sentry";
import {myListingsEnabled}  from "@Common/logic/my-listings-enabled";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {Types}              from "@Common/types";
import {Responsive}         from "@Common/ui-components/responsive";
import {setNestedValue}     from "@Common/utils/arr-path";
import {equalDeep, isEmpty} from "@Common/utils/objects";
import {parseUrlQuery}      from "@Common/utils/parse-url-query";

import {BRANCH_LEVEL, COMPANY_LEVEL} from "../common/logic/user-level";
import {SubHeader}                   from "../common/sub-header/sub-header";
import {Layout}                      from "../layout/layout";

import {InfiniteLoader}        from "./common/infinite-loader";
import {ListingRecordsDesktop} from "./desktop/listing-records-desktop";
import {EmptyListings}         from "./empty-listings";
import {ListingRecordsMobile}  from "./mobile/listing-records-mobile";
import {MyListingsErrorModal}  from "./my-listings-error-modal/my-listings-error-modal";
import {MyListingsLoading}     from "./my-listings-loading";
import {MyListingsPrompt}      from "./my-listings-loading-modal/my-listings-prompt";
import {SearchOptions}         from "./search-options/search-options";

export const MyListingsRoute = () => cs(
    ["loadingStatus", (_, next) => MyListingsLoading({next})],
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("session"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ["searchOptions", (_, next) => SearchOptions({next, filterInitValue: parseUrlQuery(window.location.search)?.search || ""})],
    ({company, session, userLevels}, next) => (
        !myListingsEnabled({company, session, userLevels}) ? (
            <Redirect to="/" />
        ) : (
            next()
        )
    ),
    ({apis, session}, next) => apis.listings == null ? (
        <div className="" xd-qa-type="label" xd-qa-name="Loading">
            Loading listings server address...
        </div>
    ) : session.loading ? (
        <div className="" xd-qa-type="label" xd-qa-name="Loading">
            Loading user session info...
        </div>
    ) : (
        next()
    ),

    ["loader", ({searchOptions, session, apis, company}, next) => InfiniteLoader({
        // step: 10,
        key: JSON.stringify({
            filter: searchOptions.options.filter,
            emulation: session.value,
            sorting: searchOptions.options.sorting,
        }),

        getTotalCount: (result) => result.totalCount,
        getList: (result) => result.records,
        fetchRange: async (range) => await apis.listings.getMyListings({
            filter : searchOptions.options.filter,
            emulation: session.value,
            sorting: searchOptions.options.sorting,
            skip: range.from,
            limit: range.to-range.from,
            allListingsSearch: company?.prefs?.allowUserLevelAllListingsSearch,
        }),
        next,
    })],

    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            sm: 768,
        },
        next,
    }}/>],
    ["myListingsLoadingModal", (_, next) => MyListingsPrompt({next})],
    ["myListingsErrorModal", (_, next) => MyListingsErrorModal({next})],
    ["loadingStatus", (_, next) => MyListingsLoading({next})],
    ({responsive, searchOptions, session, loader, apis, loadingStatus, myListingsLoadingModal, userLevels, myListingsErrorModal, routerHistory}) => (
        <Layout
            active={"my listings"}
            className={cx("my-listings-route-9ju")}
        >
            {loadingStatus.active ? (
                <div className="center">
                    {loadingStatus.message}
                </div>
            ) : (<>
                {SubHeader({
                    backLink: {
                        to: "/",
                        label: "Home",
                    },
                    title: "My Listings",
                })}

                {searchOptions.render({totalCount: loader.totalCount})}

                <div className="restrict-width-1">
                    {!loader.loading && !searchOptions.options.filter && !loader.list[0] ? (
                        <EmptyListings/>
                    ) : !loader.loading && !loader.list[0] ? (
                        <div className="no-result">
                            No record(s) found
                        </div>
                    ) : <>
                        {(() => {
                            const layout = responsive.lt("sm") ? ListingRecordsMobile : ListingRecordsDesktop;

                            return layout({
                                viewType: searchOptions.viewType,
                                listings: loader.list.map(l =>
                                    setNestedValue(
                                        l,
                                        ['thumbPictures'],
                                        l.thumbPictures?.map(p => // use image proxy on http thumbnail urls for display
                                            p.startsWith('http://') ? 'https://imgproxy.xpressdocs.com/image/jpeg/400x400/' + p : p
                                        ) ?? []
                                    )
                                ),
                                onCreateMarketingMaterials: async (listing) => {
                                    let postgresListing: Types.Apis.Models.Listing = await apis.listings.getListing({idUnique: listing.idUnique});
                                    if(isEmpty(postgresListing)) {
                                        myListingsErrorModal.show();
                                        try {
                                            XdSentry.addBreadcrumb({
                                                category: "listing",
                                                message: "Listing with ID: " + listing.idUnique + " did not load from postgres",
                                                level: XdSentry.Severity.Error,
                                                data: listing
                                            });
                                            XdSentry.captureMessage("Listing Error", XdSentry.Severity.Log);
                                        } catch (error) {
                                            console.log('There was an error attempting to log listing to sentry: ' + error.toString() );
                                        }
                                    } else {
                                        sessionStorage.setItem("listingUniqueId", listing.idUnique);
                                        sessionStorage.setItem("listingStreetAddress", listing.streetAddress);
                                        listing.idAgent && sessionStorage.setItem("listingIdAgent", listing.idAgent);
                                        listing.idBranch && sessionStorage.setItem("listingIdBranch", listing.idBranch);
                                        sessionStorage.setItem("listingId", "1");
                                        sessionStorage.setItem("listingRequestType", "template pref");
                                        sessionStorage.setItem("exclusiveContent", postgresListing.exclusiveContent)
                                        await myListingsLoadingModal();
                                        loadingStatus.loadingListing();

                                        if (userLevels?.userLevel === COMPANY_LEVEL) {
                                            const newEmu = {
                                                branchId: listing.idBranch,
                                                agentId: listing.idAgent
                                            };
                                            session.applyEmulation(newEmu);
                                            loadingStatus.emulating();

                                            newEmu.agentId === listing.idAgent ? (
                                                apis.next.session.setSessionListing(listing)
                                            ) : (
                                                apis.next.session.setSessionListing({...listing, idBranch: null, idAgent: null})
                                            );

                                        } else if (userLevels?.userLevel === BRANCH_LEVEL) {
                                            const idBranch = session.data?.branch?.idBranch;
                                            const newEmu = {
                                                branchId: idBranch,
                                                agentId: listing.idAgent
                                            };
                                            session.applyEmulation(newEmu);
                                            loadingStatus.emulating();

                                            newEmu.agentId === listing.idAgent ? (
                                                apis.next.session.setSessionListing({...listing, idBranch})
                                            ) : (
                                                apis.next.session.setSessionListing({...listing, idBranch, idAgent: null})
                                            );

                                        } else {
                                            loadingStatus.emulating();
                                            apis.next.session.setSessionListing({
                                                ...listing,
                                                idBranch: session.data?.branch?.idBranch,
                                                idAgent: session.data?.agent?.idAgent
                                            });
                                        }

                                        routerHistory.push("/");
                                    }
                                },
                            });
                        })()}

                        {loader.hasMore && (
                            <div className="load-more">
                                <button
                                    onClick={() => loader.loadMore()}
                                    xd-qa-type="button"
                                    xd-qa-name="Load More"
                                >
                                    Load More
                                    {" "}
                                    {loader.loading && (
                                        <i className="fa fa-spinner fa-pulse"/>
                                    )}
                                </button>
                            </div>
                        )}
                    </>}
                </div>
            </>)}
        </Layout>
    )
);
